import Cookies from 'js-cookie'
import { handleSessionExpired } from '@/lib/authToken'
import type { Booking, CalendarChoice, CalendarOptions, CreateBookingInput } from '../types/index.js'

const API_BASE = (import.meta.env?.VITE_API_URL as string) || ''

function buildUrl(path: string): string {
  if (API_BASE) return `${API_BASE.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  return path.startsWith('/') ? path : `/${path}`
}

function authHeaders(): Record<string, string> {
  const token = Cookies.get('token')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

async function handle<T>(res: Response): Promise<T> {
  const token = Cookies.get('token')
  if (res.status === 401 && token) { handleSessionExpired(); throw new Error('Session expired') }
  if (!res.ok) {
    const payload = await res.json().catch(() => null) as { error?: { code?: string; message?: string } } | null
    throw new Error(payload?.error?.message || 'Request failed')
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function getCalendarOptions(workspaceId: string): Promise<CalendarOptions> {
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/bookings/calendar-options`), { headers: authHeaders() })
  return handle<CalendarOptions>(res)
}

export async function setMainCalendarProvider(workspaceId: string, provider: CalendarChoice): Promise<void> {
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/bookings/calendar-preference`), {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider }),
  })
  return handle<void>(res)
}

export async function createBooking(workspaceId: string, input: CreateBookingInput): Promise<Booking> {
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/bookings`), {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  return handle<Booking>(res)
}

// Lists the rep's own bookings with start in [from, to) — used by the
// Calendar UI to correlate a rendered calendar event back to a Booking that
// only succeeded on one provider leg (Booking.degraded).
export async function listBookings(workspaceId: string, from: string, to: string): Promise<Booking[]> {
  const params = new URLSearchParams({ from, to })
  const res = await fetch(buildUrl(`/api/workspaces/${workspaceId}/bookings?${params.toString()}`), { headers: authHeaders() })
  return handle<Booking[]>(res)
}
